import React, { useState } from 'react'
import { ApkInfo } from '@/types'
import { FileInput } from '@/components/ui/FileInput'
import { Button } from '@/components/ui/Button'
import { toastError, toastSuccess } from '@/utils/toast'
import { getApkSourceLabel } from '@/utils/apk-utils'

interface DefaultApkSectionProps {
  currentApk: ApkInfo | null
  onUpload: (file: File) => Promise<void>
  isUploading: boolean
  readOnly?: boolean
}

export const DefaultApkSection: React.FC<DefaultApkSectionProps> = ({
  currentApk,
  onUpload,
  isUploading,
  readOnly = false,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null)

  const handleFileChange = (files: FileList | null) => {
    const file = files?.[0]
    if (!file) return

    if (!file.name.toLowerCase().endsWith('.apk')) {
      toastError('Seuls les fichiers .apk sont autorisés')
      return
    }

    setSelectedFile(file)
  }

  const handleFileRemove = () => {
    setSelectedFile(null)
  }

  const handleSubmit = async () => {
    if (!selectedFile) return

    try {
      await onUpload(selectedFile)
      toastSuccess('APK standard mise à jour avec succès')
      setSelectedFile(null)
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Erreur lors du téléchargement de l'APK"
      toastError(errorMessage)
    }
  }

  return (
    <div className="bg-white rounded-lg border border-[var(--color-border)] shadow-sm p-6">
      <div className="mb-4">
        <h3 className="text-lg font-medium text-[var(--color-text-primary)]">
          APK standard NeoSVPlus
        </h3>
        <p className="text-sm text-[var(--color-text-secondary)] mt-1">
          Fichier de référence utilisé par les grossistes et revendeurs directs sans APK marque
          blanche. Un nouvel upload remplace la version précédente.
        </p>
      </div>

      <div className="space-y-4">
        <div className="p-3 bg-[var(--color-gray-100)] rounded-lg">
          {currentApk?.filename ? (
            <div className="text-sm text-[var(--color-text-primary)] space-y-1">
              <p>
                Fichier actuel : <span className="font-medium">{currentApk.filename}</span>
              </p>
              <p className="text-[var(--color-text-secondary)]">
                {getApkSourceLabel(currentApk.source)}
                {currentApk.updatedAt
                  ? ` — mise à jour le ${new Date(currentApk.updatedAt).toLocaleString('fr-FR')}`
                  : ''}
              </p>
            </div>
          ) : (
            <p className="text-sm text-[var(--color-text-muted)]">Aucune APK standard disponible</p>
          )}
        </div>

        {!readOnly && (
          <>
            <FileInput
              label="Téléverser une nouvelle APK standard"
              accept=".apk,application/vnd.android.package-archive"
              onChange={handleFileChange}
              disabled={isUploading}
              selectedFiles={selectedFile ? [selectedFile] : []}
              onFileRemove={handleFileRemove}
              helperText="Fichier .apk uniquement (max. 200 Mo)"
            />
            <div className="flex justify-end">
              <Button onClick={handleSubmit} isLoading={isUploading} disabled={!selectedFile}>
                Enregistrer l&apos;APK standard
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
